import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import RemoveIcon from "@mui/icons-material/Remove";
import {
  Avatar,
  Box,
  Chip,
  Button,
  Dialog,
  Divider,
  IconButton,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import React, { useEffect, useRef } from "react";
import { default_Photo, playerTeamLogo } from "../../../Assets";
import { useAuthStore } from "../../../store";
import {
  getAuctionRules,
  normalizeCategoryForRules,
  resolveCategoryWithFallback,
} from "../../../Utils/auctionRules";
import { mapHighestLevelToCategory } from "../../../Utils/helperFunctions";

const BidPriceModal = ({
  open,
  onClose,
  player,
  bid,
  setBid,
  onFinalize,
  onUnsold,
  teams,
  selectedTeam,
  setSelectedTeam,
  onConfirm,
  teamdetailsWithStatus,
}) => {
  const [confirmUnsold, setConfirmUnsold] = React.useState(false);
  const [confirmFinalize, setConfirmFinalize] = React.useState(false);
  const [bidTouched, setBidTouched] = React.useState(false);
  const [hasIncremented, setHasIncremented] = React.useState(false);
  const contentScrollRef = useRef(null);
  // const [debouncedInput, setDebouncedInput] = useState(bid);
  const basePrice = player?.basePrice || 0;
  const activeRules = getAuctionRules(player?.gender);
  const playerMcaNo =
    player?.MCANo ||
    player?.MCA_No__c ||
    player?.MCA_Registration_Number__c ||
    player?.Player__r?.MCA_No__c ||
    player?.Player__r?.MCA_Registration_Number__c;
  const rawCategory = resolveCategoryWithFallback({
    division: activeRules?.division === "women" ? "women" : "men",
    mcaNo: playerMcaNo,
    fallbackCategory:
      player?.category ||
      mapHighestLevelToCategory(
        player?.recentCompetetionLevel || player?.highest_level_played,
      ) ||
      "",
  });
  const normalizedCategory = normalizeCategoryForRules(
    rawCategory,
    activeRules,
    {
      playerAge: player?.playerAge,
      birthData: player?.birthData || player?.playerBirthDate,
    },
  );
  const categoryBasePrice =
    activeRules?.basePrices?.[normalizedCategory] || player?.basePrice || 0;
  const divisionLabel = activeRules?.division === "women" ? "WOMEN'S" : "MEN'S";
  const isIconPlayer =
    player?.recentCompetetionLevel === "Indian senior team" ||
    player?.recentCompetitionLevel === "Indian senior team";
  const isDirectIconAllocation =
    isIconPlayer && activeRules?.division !== "women";

  const livePlayerPayload = {
    ...(player || {}),
    auctionResult: "Stats",
  };
  const liveSocketStatus = isDirectIconAllocation ? "willPlayFor" : "liveBid";

  useEffect(() => {
    if (open) {
      setHasIncremented(false);
      setBidTouched(false);
      setConfirmFinalize(false);
      setConfirmUnsold(false);
    }
  }, [open, player?.playerRegId]);

  useEffect(() => {
    if (!confirmFinalize) return;
    const node = contentScrollRef.current;
    if (!node) return;
    requestAnimationFrame(() => {
      node.scrollTo({
        top: node.scrollHeight,
        behavior: "smooth",
      });
    });
  }, [confirmFinalize]);

  // useEffect(() => {
  //   const timeout = setTimeout(() => {
  //     if (!isNaN(debouncedInput)) {
  //       teamdetailsWithStatus(debouncedInput, player); // Debounced
  //     }
  //   }, 300);

  //   return () => clearTimeout(timeout);
  // }, [debouncedInput, player]);

  // For increments
  const getIncrementValue = (bid) => {
    if (bid < 500000) {
      return 25000;
    }
    return 50000;
  };

  // For decrements
  const getDecrementValue = (bid) => {
    if (bid > 500000) {
      return 50000;
    }
    return 25000;
  };

  const handleIncrement = async () => {
    if (!hasIncremented) {
      // First click: only send live bid update with current bid
      await useAuthStore.getState().sendLiveBidUpdate({
        player: livePlayerPayload,
        bidAmount: bid,
        status: liveSocketStatus,
        showWillPlayFor: isDirectIconAllocation,
      });
      setHasIncremented(true); // Mark that first click has occurred
      teamdetailsWithStatus(bid, player);
      return;
    }

    // Subsequent clicks: increment as usual
    const increment = getIncrementValue(bid);
    const newBid = bid + increment;
    setBid(newBid);
    setSelectedTeam(null);
    teamdetailsWithStatus(newBid, player);
    await useAuthStore.getState().sendLiveBidUpdate({
      player: livePlayerPayload,
      bidAmount: newBid,
      status: liveSocketStatus,
      showWillPlayFor: isDirectIconAllocation,
    });

    if (newBid > basePrice) {
      setBidTouched(true);
    }
  };

  const handleDecrement = async () => {
    const decrement = getDecrementValue(bid);
    const newBid = Math.max(0, bid - decrement);
    setBid(newBid);
    setSelectedTeam(null);
    teamdetailsWithStatus(newBid, player);
    await useAuthStore.getState().sendLiveBidUpdate({
      player: livePlayerPayload,
      bidAmount: newBid,
      status: "liveBid",
      showWillPlayFor: isDirectIconAllocation,
    });
  };

  const triggerInitialLiveBidAtBase = async () => {
    const initialBid = Number(player?.basePrice || 0);
    setBid(initialBid);
    teamdetailsWithStatus(initialBid, player);
    await useAuthStore.getState().sendLiveBidUpdate({
      player: livePlayerPayload,
      bidAmount: initialBid,
      status: "liveBid",
    });
    setHasIncremented(true);
    setBidTouched(true);
  };

  const formatCurrency = (amount) =>
    `₹${Number(amount || 0).toLocaleString("en-IN")}`;
  const nextIncrement = getIncrementValue(bid);
  const nextBidPreview = bid + nextIncrement;
  const disableDecrement = bid <= player?.basePrice;
  const totalTeams = Array.isArray(teams) ? teams.length : 0;
  const eligibleTeamCount = Array.isArray(teams)
    ? teams.filter((team) => team?.teamStatus?.valid === true).length
    : 0;

  return (
    <Dialog
      open={open}
      onClose={() => {
        setBidTouched(false);
        setConfirmUnsold(false);
        setHasIncremented(false);
        setSelectedTeam(null);
        onClose();
      }}
      maxWidth="md"
      PaperProps={{
        sx: {
          background:
            "linear-gradient(180deg, rgba(11,11,12,0.98) 0%, rgba(6,6,8,0.98) 100%)",
          border: "1px solid rgba(255, 255, 255, 0.14)",
          borderRadius: "18px",
          width: "min(980px, calc(100vw - 48px))",
          maxWidth: "980px",
          maxHeight: "calc(100dvh - 32px)",
          p: 0,
          overflow: "auto",
        },
      }}
    >
      <Box
        sx={{
          px: { xs: 2.5, md: 3.5 },
          py: 2.2,
          background:
            "linear-gradient(90deg, rgba(255,126,0,0.18) 0%, rgba(252,74,118,0.06) 100%)",
          borderBottom: "1px solid rgba(255,255,255,0.14)",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Box>
          <Typography
            sx={{ color: "#fff", fontWeight: 800, fontSize: "1.35rem" }}
          >
            {confirmUnsold ? "Mark As Unsold" : "Live Bid Control"}
          </Typography>
          <Typography
            sx={{ color: "rgba(255,255,255,0.75)", fontSize: "0.88rem" }}
          >
            {isDirectIconAllocation
              ? `${divisionLabel} | Icon Player | Will play for selected team`
              : `${divisionLabel} | ${normalizedCategory || "Player Auction"} | Increment rules enabled`}
          </Typography>
        </Box>
        <IconButton onClick={onClose} sx={{ color: "#fff", p: 0.4 }}>
          <CloseIcon />
        </IconButton>
      </Box>

      <Box
        ref={contentScrollRef}
        sx={{
          px: { xs: 2.5, md: 3.5 },
          py: { xs: 2, md: 2.4 },
          maxHeight: "calc(100dvh - 180px)",
          overflowY: "auto",
          pr: { xs: 2, md: 2.5 },
        }}
      >
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "340px 1fr" },
            gap: 2,
            alignItems: "stretch",
          }}
        >
          <Box
            sx={{
              border: "1px solid rgba(255,255,255,0.12)",
              borderRadius: "14px",
              p: 2.2,
              background:
                "linear-gradient(180deg, rgba(255,255,255,0.06) 0%, rgba(255,255,255,0.02) 100%)",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Avatar
              src={player?.photo ? player?.photo : default_Photo}
              alt={player?.name}
              sx={{
                width: 112,
                height: 112,
                mb: 1.4,
                border: "1px solid rgba(255,255,255,0.2)",
              }}
            />
            <Typography
              sx={{
                fontWeight: 700,
                fontSize: "2rem",
                color: "#fff",
                textAlign: "center",
                lineHeight: 1.1,
              }}
            >
              {player?.name}
            </Typography>
            <Typography sx={{ color: "rgba(255,255,255,0.7)", mb: 1.5 }}>
              {player?.MCANo}
            </Typography>
            <Box
              sx={{
                display: "flex",
                gap: 1,
                flexWrap: "wrap",
                justifyContent: "center",
              }}
            >
              <Chip
                size="small"
                label={player?.role || "Role"}
                sx={{ color: "#fff", background: "rgba(255,255,255,0.1)" }}
              />
              <Chip
                size="small"
                label={normalizedCategory || "Category"}
                sx={{ color: "#fff", background: "rgba(255,255,255,0.1)" }}
              />
            </Box>
          </Box>

          <Box
            sx={{
              border: "1px solid rgba(255,255,255,0.12)",
              borderRadius: "14px",
              p: 2.2,
              background:
                "linear-gradient(180deg, rgba(255,255,255,0.05) 0%, rgba(255,255,255,0.015) 100%)",
            }}
          >
            {isDirectIconAllocation ? (
              <Box
                sx={{
                  minHeight: "100%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  textAlign: "center",
                }}
              >
                <Box>
                  <Typography
                    sx={{
                      color: "#fff",
                      fontWeight: 800,
                      fontSize: "1.55rem",
                      mb: 0.8,
                    }}
                  >
                    Will Play For
                  </Typography>
                  <Typography
                    sx={{ color: "rgba(255,255,255,0.8)", fontSize: "1rem" }}
                  >
                    Select a team below and click <b>Finalize Bid</b>
                  </Typography>
                </Box>
              </Box>
            ) : (
              <>
                <Typography sx={{ color: "#fff", fontWeight: 700, mb: 1.2 }}>
                  Bid Controls
                </Typography>
                <Typography
                  sx={{
                    color: "rgba(255,255,255,0.72)",
                    fontSize: "0.82rem",
                    mb: 1.1,
                  }}
                >
                  Eligible Teams: {eligibleTeamCount} / {totalTeams}
                </Typography>
                <Box display="flex" gap={1.2} mt={1}>
                  <IconButton
                    onClick={handleDecrement}
                    disabled={disableDecrement}
                    sx={{
                      height: "56px",
                      width: "60px",
                      border: "1px solid rgba(255,255,255,0.35)",
                      color: "#fff",
                      borderRadius: "12px",
                      background: "rgba(255,255,255,0.08)",
                      "&:hover": { background: "rgba(255,255,255,0.15)" },
                      opacity: disableDecrement ? 0.45 : 1,
                    }}
                  >
                    <RemoveIcon />
                  </IconButton>
                  <TextField
                    type="text"
                    value={bid.toLocaleString("en-IN")}
                    inputProps={{
                      sx: {
                        textAlign: "center",
                        color: "#fff",
                        fontSize: "1.95rem",
                        fontWeight: 800,
                        letterSpacing: 0.2,
                      },
                    }}
                    sx={{
                      flex: 1,
                      "& .MuiInputBase-input": {
                        backgroundColor: "rgba(255,255,255,0.14)",
                        height: "22px",
                        borderRadius: "12px",
                        border: "1px solid rgba(255,255,255,0.35)",
                      },
                      "& fieldset": { border: "none" },
                    }}
                    onChange={async (e) => {
                      let value = Number(e.target.value.replace(/[^0-9]/g, ""));
                      if (isNaN(value)) value = 0;
                      setBid(value);
                      teamdetailsWithStatus(value, player);
                    }}
                  />
                  <IconButton
                    onClick={handleIncrement}
                    sx={{
                      height: "56px",
                      width: "60px",
                      border: "1px solid rgba(255,255,255,0.35)",
                      color: "#fff",
                      borderRadius: "12px",
                      background:
                        "linear-gradient(135deg, rgba(255,126,0,0.42) 0%, rgba(252,74,118,0.42) 100%)",
                      "&:hover": {
                        background:
                          "linear-gradient(135deg, rgba(255,126,0,0.6) 0%, rgba(252,74,118,0.6) 100%)",
                      },
                    }}
                  >
                    <AddIcon />
                  </IconButton>
                </Box>

                <Box
                  sx={{
                    mt: 1.4,
                    display: "grid",
                    gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
                    gap: 1,
                  }}
                >
                  <Box
                    sx={{
                      p: 1,
                      borderRadius: 1.5,
                      border: "1px solid rgba(255,255,255,0.14)",
                      background: "rgba(255,255,255,0.04)",
                    }}
                  >
                    <Typography
                      sx={{
                        color: "rgba(255,255,255,0.65)",
                        fontSize: "0.74rem",
                        textTransform: "uppercase",
                      }}
                    >
                      Base Price
                    </Typography>
                    <Typography
                      sx={{
                        color: "#fff",
                        fontWeight: 700,
                        fontSize: "0.98rem",
                      }}
                    >
                      {formatCurrency(basePrice)}
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      p: 1,
                      borderRadius: 1.5,
                      border: "1px solid rgba(255,255,255,0.14)",
                      background: "rgba(255,255,255,0.04)",
                    }}
                  >
                    <Typography
                      sx={{
                        color: "rgba(255,255,255,0.65)",
                        fontSize: "0.74rem",
                        textTransform: "uppercase",
                      }}
                    >
                      Next Increment
                    </Typography>
                    <Typography
                      sx={{
                        color: "#fff",
                        fontWeight: 700,
                        fontSize: "0.98rem",
                      }}
                    >
                      {formatCurrency(nextIncrement)}
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      p: 1,
                      borderRadius: 1.5,
                      border: "1px solid rgba(255,255,255,0.14)",
                      background: "rgba(255,255,255,0.04)",
                    }}
                  >
                    <Typography
                      sx={{
                        color: "rgba(255,255,255,0.65)",
                        fontSize: "0.74rem",
                        textTransform: "uppercase",
                      }}
                    >
                      Next Bid
                    </Typography>
                    <Typography
                      sx={{
                        color: "#fff",
                        fontWeight: 700,
                        fontSize: "0.98rem",
                      }}
                    >
                      {formatCurrency(nextBidPreview)}
                    </Typography>
                  </Box>
                </Box>

                <Divider
                  sx={{ my: 1.4, borderColor: "rgba(255,255,255,0.12)" }}
                />
                <Typography sx={{ color: "#fff", fontWeight: 700, mb: 0.8 }}>
                  Auction Rules
                </Typography>
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: {
                      xs: "1fr",
                      md: "repeat(2, minmax(0,1fr))",
                    },
                    gap: 1,
                  }}
                >
                  <Typography
                    sx={{
                      color: "rgba(255,255,255,0.82)",
                      fontSize: "0.84rem",
                    }}
                  >
                    Increment upto ₹5,00,000: <b>₹25,000</b>
                  </Typography>
                  <Typography
                    sx={{
                      color: "rgba(255,255,255,0.82)",
                      fontSize: "0.84rem",
                    }}
                  >
                    Increment above ₹5,00,000: <b>₹50,000</b>
                  </Typography>
                  <Typography
                    sx={{
                      color: "rgba(255,255,255,0.82)",
                      fontSize: "0.84rem",
                    }}
                  >
                    Category base: <b>{formatCurrency(categoryBasePrice)}</b>
                  </Typography>
                  <Typography
                    sx={{
                      color: "rgba(255,255,255,0.82)",
                      fontSize: "0.84rem",
                    }}
                  >
                    Squad limit:{" "}
                    <b>
                      {activeRules.squadMin}-{activeRules.squadMax}
                    </b>
                  </Typography>
                </Box>
              </>
            )}
          </Box>
        </Box>

        {!confirmUnsold ? (
          <Box sx={{ width: "100%", mt: 2 }}>
            <Typography sx={{ color: "#fff", fontWeight: 700, mb: 1 }}>
              Eligible Teams
            </Typography>
            <Box
              sx={{
                border: "1px solid rgba(255,255,255,0.12)",
                borderRadius: "14px",
                p: 1.6,
                background: "rgba(255,255,255,0.02)",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  flexWrap: "wrap",
                  justifyContent: "center",
                  gap: 1.4,
                  maxWidth: "100%",
                  mx: "auto",
                }}
              >
                {teams
                  ?.slice()
                  .sort((a, b) => a.Name.localeCompare(b.Name))
                  .map((team) => {
                    const isSelected = selectedTeam?.Name === team?.Name;
                    const noFund = team?.teamStatus?.status === "No Fund";
                    const isEligible = team?.teamStatus?.valid === true;
                    const noFundMessage =
                      team?.teamStatus?.message ||
                      "Insufficient fund / rule limit reached";

                    return (
                      <Tooltip
                        key={team?.Name}
                        placement="top"
                        arrow
                        title={noFund ? `No Fund: ${noFundMessage}` : ""}
                      >
                        <Box
                          onClick={() => {
                            setSelectedTeam(team);
                          }}
                          sx={{
                            width: { xs: 84, md: 90 },
                            height: { xs: 84, md: 90 },
                            borderRadius: "50%",
                            p: "6px",
                            cursor: "pointer",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            overflow: "hidden",
                            position: "relative",
                            backgroundColor: noFund
                              ? "rgba(181, 35, 35, 0.75)"
                              : "rgba(13, 13, 13, 0.95)",
                            border: isSelected
                              ? "2px solid #FF8C00"
                              : "1px solid rgba(255,255,255,0.5)",
                            boxShadow: isSelected
                              ? "0 0 0 2px rgba(255,140,0,0.22)"
                              : "none",
                            transition: "all 0.15s ease",
                            opacity: 1,
                          }}
                        >
                          <img
                            src={team?.Logo_URL__c || playerTeamLogo}
                            alt={team?.Name}
                            style={{
                              width: "100%",
                              height: "100%",
                              objectFit: "contain",
                            }}
                          />
                          {noFund && (
                            <Box
                              sx={{
                                position: "absolute",
                                bottom: "4px",
                                left: "50%",
                                transform: "translateX(-50%)",
                                px: 0.8,
                                py: 0.15,
                                borderRadius: "8px",
                                fontSize: "0.55rem",
                                fontWeight: 800,
                                letterSpacing: 0.3,
                                textTransform: "uppercase",
                                color: "#fff",
                                background: "rgba(167, 22, 22, 0.95)",
                                border: "1px solid rgba(255,255,255,0.22)",
                                whiteSpace: "nowrap",
                              }}
                            >
                              No Fund
                            </Box>
                          )}
                        </Box>
                      </Tooltip>
                    );
                  })}
              </Box>
            </Box>
          </Box>
        ) : (
          <Typography
            sx={{
              fontSize: "20px",
              fontWeight: 500,
              textAlign: "center",
              color: "#fff",
            }}
          >
            Are you sure, you want to mark this player as unsold?
          </Typography>
        )}

        {confirmUnsold ? (
          <Box
            sx={{
              position: "sticky",
              bottom: 0,
              zIndex: 5,
              mt: 2.4,
              pt: 1.2,
              pb: 0.4,
              background:
                "linear-gradient(180deg, rgba(10,10,12,0) 0%, rgba(10,10,12,0.92) 26%, rgba(10,10,12,0.98) 100%)",
            }}
          >
            <Box display="flex" width={"100%"} gap={2}>
              <Button
                variant="outlined"
                onClick={() => setConfirmUnsold(false)}
                // onClick={onUnsold}
                sx={{
                  height: "52px",
                  borderColor: "#fff",
                  color: "#fff",
                  px: 3,
                  borderRadius: "8px",
                  flex: 1,
                  fontSize: "16px",
                  fontWeight: "500",
                  background: "#0D0D0D",
                  "&:hover": {
                    borderColor: "#fff",
                    background: "#0D0D0D",
                  },
                }}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={onUnsold}
                sx={{
                  height: "52px",
                  background: "#fff",
                  color: "#000",
                  px: 3,
                  borderRadius: "8px",
                  fontSize: "16px",
                  fontWeight: "500",
                  flex: 1,
                  "&:hover": {
                    background: "#fff",
                  },
                }}
              >
                Mark as unsold
              </Button>
            </Box>
          </Box>
        ) : confirmFinalize ? (
          <>
            <Box
              sx={{
                mt: 1.2,
                border: "1px solid rgba(255,255,255,0.14)",
                borderRadius: "14px",
                background:
                  "linear-gradient(180deg, rgba(255,255,255,0.06) 0%, rgba(255,255,255,0.02) 100%)",
                px: { xs: 1.4, md: 2 },
                py: { xs: 1.2, md: 1.6 },
              }}
            >
              <Typography
                sx={{
                  fontSize: { xs: "18px", md: "20px" },
                  fontWeight: 700,
                  textAlign: "center",
                  color: "#fff",
                }}
              >
                Confirm Finalize Bid
              </Typography>
              <Typography
                sx={{
                  fontSize: { xs: "13px", md: "14px" },
                  fontWeight: 400,
                  textAlign: "center",
                  color: "rgba(255,255,255,0.7)",
                  mt: 0.4,
                }}
              >
                Please verify the team and final amount.
              </Typography>

              <Box
                sx={{
                  mt: 1.4,
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
                  gap: 1,
                }}
              >
                <Box
                  sx={{
                    border: "1px solid rgba(255,255,255,0.14)",
                    borderRadius: "10px",
                    p: "10px 12px",
                    background: "rgba(255,255,255,0.03)",
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: "11px",
                      color: "rgba(255,255,255,0.62)",
                      textTransform: "uppercase",
                      letterSpacing: "0.06em",
                    }}
                  >
                    Sold To
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: "15px",
                      color: "#fff",
                      fontWeight: 600,
                      mt: 0.25,
                    }}
                  >
                    {selectedTeam?.Name || "Selected team"}
                  </Typography>
                </Box>
                <Box
                  sx={{
                    border: "1px solid rgba(255,255,255,0.14)",
                    borderRadius: "10px",
                    p: "10px 12px",
                    background: "rgba(255,255,255,0.03)",
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: "11px",
                      color: "rgba(255,255,255,0.62)",
                      textTransform: "uppercase",
                      letterSpacing: "0.06em",
                    }}
                  >
                    Final Bid
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: "19px",
                      color: "#fff",
                      fontWeight: 800,
                      mt: 0.15,
                    }}
                  >
                    {formatCurrency(bid)}
                  </Typography>
                </Box>
              </Box>
            </Box>
            <Box
              sx={{
                position: "sticky",
                bottom: 0,
                zIndex: 5,
                mt: 2.4,
                pt: 1.2,
                pb: 0.4,
                background:
                  "linear-gradient(180deg, rgba(10,10,12,0) 0%, rgba(10,10,12,0.92) 26%, rgba(10,10,12,0.98) 100%)",
              }}
            >
              <Box display="flex" width={"100%"} gap={2}>
                <Button
                  variant="outlined"
                  onClick={() => setConfirmFinalize(false)}
                  sx={{
                    height: "52px",
                    borderColor: "#fff",
                    color: "#fff",
                    px: 3,
                    borderRadius: "8px",
                    flex: 1,
                    fontSize: "16px",
                    fontWeight: "500",
                    background: "#0D0D0D",
                    "&:hover": {
                      borderColor: "#fff",
                      background: "#0D0D0D",
                    },
                  }}
                >
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  onClick={onConfirm}
                  sx={{
                    height: "52px",
                    background:
                      "linear-gradient(90deg, rgba(255,126,0,1) 0%, rgba(252,74,118,1) 100%)",
                    color: "#fff",
                    px: 3,
                    borderRadius: "8px",
                    fontSize: "16px",
                    fontWeight: "500",
                    flex: 1,
                    "&:hover": {
                      background:
                        "linear-gradient(90deg, rgba(255,126,0,1) 0%, rgba(252,74,118,1) 100%)",
                    },
                  }}
                >
                  Yes, finalize
                </Button>
              </Box>
            </Box>
          </>
        ) : (
          <Box
            sx={{
              position: "sticky",
              bottom: 0,
              zIndex: 5,
              mt: 2.4,
              pt: 1.2,
              pb: 0.4,
              background:
                "linear-gradient(180deg, rgba(10,10,12,0) 0%, rgba(10,10,12,0.92) 26%, rgba(10,10,12,0.98) 100%)",
            }}
          >
            <Box display="flex" width={"100%"} gap={2}>
              <Button
                variant="outlined"
                onClick={() => setConfirmUnsold(true)}
                disabled={bidTouched}
                // onClick={onUnsold}
                sx={{
                  height: "52px",
                  borderColor: "#fff",
                  color: "#fff",
                  px: 3,
                  borderRadius: "8px",
                  flex: 1,
                  fontSize: "16px",
                  fontWeight: "500",
                  background: "#0D0D0D",
                  "&:hover": {
                    borderColor: "#fff",
                    background: "#0D0D0D",
                  },
                  "&.Mui-disabled": {
                    color: "#fff",
                    borderColor: "#fff",
                    opacity: 0.5,
                  },
                }}
              >
                Mark as unsold
              </Button>
              <Button
                variant="contained"
                onClick={async () => {
                  if (isDirectIconAllocation) {
                    if (!selectedTeam) return;
                    if (!hasIncremented) {
                      await triggerInitialLiveBidAtBase();
                      return;
                    }
                    setConfirmFinalize(true);
                    return;
                  }
                  if (selectedTeam) {
                    if (!hasIncremented) {
                      await triggerInitialLiveBidAtBase();
                      return;
                    }
                    setConfirmFinalize(true);
                    return;
                  }
                  return;
                }}
                disabled={
                  isDirectIconAllocation ? !selectedTeam : !selectedTeam
                }
                sx={{
                  height: "52px",
                  background:
                    "linear-gradient(90deg, rgba(255,126,0,1) 0%, rgba(252,74,118,1) 100%)",
                  color: "#fff",
                  px: 3,
                  borderRadius: "8px",
                  fontSize: "16px",
                  fontWeight: "500",
                  flex: 1,
                  "&:hover": {
                    background:
                      "linear-gradient(90deg, rgba(255,126,0,1) 0%, rgba(252,74,118,1) 100%)",
                  },
                  "&.Mui-disabled": {
                    background:
                      "linear-gradient(90deg, rgba(255,126,0,1) 0%, rgba(252,74,118,1) 100%)",
                    color: "#fff",
                    opacity: 0.45,
                  },
                }}
              >
                Finalize bid
              </Button>
            </Box>
          </Box>
        )}
      </Box>
    </Dialog>
  );
};

export default BidPriceModal;
